import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Container } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LandingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '90vh',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  padding: theme.spacing(4, 2),
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  maxWidth: 800,
  width: '90%',
  textAlign: 'center',
  animation: `${fadeIn} 1s ease-out`,
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '4.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  letterSpacing: '2px',
  [theme.breakpoints.down('md')]: {
    fontSize: '3rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  maxWidth: 600,
  margin: `0 auto ${theme.spacing(6)}`,
  lineHeight: 1.6,
  opacity: 0.9,
  fontWeight: 300,
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(4),
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  flexWrap: 'wrap',
}));

const Button = styled(Link)(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(1.5, 4),
  borderRadius: 50,
  textDecoration: 'none',
  fontWeight: theme.typography.fontWeightBold,
  fontSize: '1.1rem',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.standard,
  }),
  textTransform: 'uppercase',
  letterSpacing: '1px',
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    padding: theme.spacing(1.25, 3),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1, 2.5),
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
    backgroundColor: theme.palette.grey[100],
  },
  '&:focus-visible': {
    outline: '3px solid #4a90e2',
    outlineOffset: '2px',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: 'rgba(27, 88, 160, 0.1)',
    transform: 'translateY(-3px)',
  },
  '&:focus-visible': {
    outline: '3px solid #4a90e2',
    outlineOffset: '2px',
  },
}));


function LandingPage() {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('token');
  
  if (isLoggedIn) {
    navigate('/dashboard');
    return null;
  }

  return (
    <LandingWrapper>
      <ContentContainer>
        <Title>pray4all</Title>
        <Subtitle>
          Connect with people around the world through the power of prayer.
          Share your thoughts, support others, and experience the strength of a global spiritual community.
        </Subtitle>
        <ButtonContainer>
          <CTAButton to="/login?tab=register">Get Started</CTAButton>
          <SecondaryButton to="/login?tab=login">Log In</SecondaryButton>
        </ButtonContainer>
      </ContentContainer>
    </LandingWrapper>
  );
}

export default LandingPage;